import {
  GemSpec,
  BaseProcess,
  BaseProcessMetadata,
  Connection,
  Connection as GraphConnection,
  GenericGraphProcesses,
  Process,
  CommonGraph
} from '@common/types';
import { useLSPMethod } from '@LSP/base/hook';
import { LSP } from '@LSP/base/types';
import { Direction } from '@prophecy/interfaces/generic';
import { toast } from '@prophecy/ui';
import { ToastUtilReturnType } from '@prophecy/ui-v3/LegacyToast/types';
import { AUTO_POSITION, NODE_DIMENSION } from '@prophecy/ui/Graph/tokens';
import { EdgeData, NodeData, NodeHandle, NodeType } from '@prophecy/ui/Graph/types';
import { getNodeRect } from '@prophecy/ui/Graph/utils';
import { getViewportGrids } from '@prophecy/utils/graph';
import { doubleId } from '@prophecy/utils/id';
import { useWatch, usePersistentCallback, useThrottle } from '@prophecy/utils/react/hooks';
import { StableChildren } from '@prophecy/utils/react/StableChildren';
import { clamp, mean, noop, throttle } from 'lodash-es';
import React, { useRef, createContext, useState, useMemo, useContext, useEffect } from 'react';
import { ConnectDropTarget, DropTargetMonitor, useDrop } from 'react-dnd';
import { Edge, Node, NodeDragHandler, NodeInternals, Rect, useReactFlow, useStoreApi, XYPosition } from 'reactflow';

import { getConnectionPath } from '../Parser/bindings';
import { spaceToUnderscore } from '../utils';
import { useSpecs } from './graph/gem-specs';
import { isCyclicGraph } from './graph/graphUtils';
import { useForwardOutputSlugs } from './graph/useForwardOutputSlugs';
import { gemSpecsToGemTypeString } from './selectors';
import { OnNodeDragStopHandler } from './utils/graph/types';

const CELL_GAP = 120;
const PROXIMITY_OFFSET = CELL_GAP + NODE_DIMENSION / 2;
const NODE_ANGLE_DELTA = 15;

export enum NodeDirection {
  'top-left' = 'top-left',
  'top-right' = 'top-right',
  'right-top' = 'right-top',
  'right-bottom' = 'right-bottom',
  'bottom-left' = 'bottom-left',
  'bottom-right' = 'bottom-right',
  'left-top' = 'left-top',
  'left-bottom' = 'left-bottom'
}

const octants = {
  '-4': NodeDirection['left-top'],
  '-3': NodeDirection['top-left'],
  '-2': NodeDirection['top-right'],
  '-1': NodeDirection['right-top'],
  '0': NodeDirection['right-bottom'],
  '1': NodeDirection['bottom-right'],
  '2': NodeDirection['bottom-left'],
  '3': NodeDirection['left-bottom']
} as const;

type RectCoordinates = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

export type NodeProximity = {
  distance: number;
  angle: number;
  octant: NodeDirection;
  nodeId: string;
  node?: Node;
};

export type SuggestionGraph = Pick<CommonGraph, 'connections' | 'processes'>;

type Suggestion = {
  nodesInProximity?: NodeProximity[];
  referenceNodeProximity?: NodeProximity;
  graph: SuggestionGraph;
};

const AutoPilotContext = createContext<{
  suggestion?: Suggestion;
  shiftKeyPressed: boolean;
  setSuggestion: (suggestion?: Suggestion) => void;
}>({
  setSuggestion: noop,
  shiftKeyPressed: false
});

export function AutoPilotProvider({
  children,
  listenShiftKey
}: {
  listenShiftKey: boolean;
  children: React.ReactNode;
}) {
  const [suggestion, setSuggestion] = useState<Suggestion>();
  const [shiftKeyPressed, setShiftKeyPressed] = useState(false);

  const providerValue = useMemo(() => {
    return {
      suggestion,
      shiftKeyPressed,
      setSuggestion
    };
  }, [suggestion, setSuggestion, shiftKeyPressed]);

  // handle the key press
  useEffect(() => {
    if (!listenShiftKey) {
      // TODO: enhance to accept keycode
      return;
    }
    const dragHandler = throttle((e: MouseEvent | KeyboardEvent) => {
      setShiftKeyPressed(e.shiftKey && !(e.ctrlKey || e.metaKey));
    }, 200);

    const keyUpHandler = (e: KeyboardEvent) => {
      setShiftKeyPressed(false);
    };

    // browser don't file key events while dragging, so we need to also listen for drag events
    window.addEventListener('dragover', dragHandler);
    window.addEventListener('keydown', dragHandler);
    window.addEventListener('keyup', keyUpHandler);

    return () => {
      window.removeEventListener('dragover', dragHandler);
      window.removeEventListener('keydown', dragHandler);
      window.removeEventListener('keyup', keyUpHandler);
    };
  }, [listenShiftKey]);

  return (
    <AutoPilotContext.Provider value={providerValue}>
      <StableChildren>{children}</StableChildren>
    </AutoPilotContext.Provider>
  );
}

export function useAutoPilot() {
  return useContext(AutoPilotContext);
}

function filterNonUsedConnectablePorts(handle: NodeHandle, usedPorts: Record<string, boolean>) {
  return handle.isConnectable !== false && !usedPorts[handle.id];
}

// for source we show drop suggestion on top, right and botton
export function getAllowedDirectionForSource() {
  return [
    NodeDirection['top-left'],
    NodeDirection['top-right'],
    NodeDirection['right-top'],
    NodeDirection['right-bottom'],
    NodeDirection['bottom-right'],
    NodeDirection['bottom-left']
  ];
}

export function getAllowedDirectionFromSpec(spec: GemSpec) {
  const allowedDirections: NodeDirection[] = [];
  const ports = spec.defaultState.ports;
  if (ports?.inputs?.length) {
    allowedDirections.push(NodeDirection['left-top'], NodeDirection['left-bottom']);
  }
  if (ports?.outputs?.length) {
    allowedDirections.push(NodeDirection['right-top'], NodeDirection['right-bottom']);
  }
  return allowedDirections;
}

export function getAllowedDirectionFromNode(node: Node<NodeData>, usedPorts: Record<string, boolean>) {
  const allowedDirections: NodeDirection[] = [];
  const hasUnconnectedOutputPorts = node.data.sourceHandles.some((port) => !usedPorts[port.id]);
  const hasUnconnectedInputPorts = node.data.targetHandles.some((port) => !usedPorts[port.id]);

  if (hasUnconnectedInputPorts) {
    allowedDirections.push(NodeDirection['left-top'], NodeDirection['left-bottom']);
  }

  if (hasUnconnectedOutputPorts) {
    allowedDirections.push(NodeDirection['right-top'], NodeDirection['right-bottom']);
  }

  return allowedDirections;
}

function getNodeRectMap(nodes: Node<NodeData>[], nodeInternals: NodeInternals) {
  return Object.fromEntries(nodes.map((node) => [node.id, getNodeRect(node, nodeInternals)]));
}

const getProximityRectCoordinates = ({ x, y, width, height }: Rect) => {
  return {
    x1: x - PROXIMITY_OFFSET,
    y1: y - PROXIMITY_OFFSET,
    x2: x + width + PROXIMITY_OFFSET,
    y2: y + height + PROXIMITY_OFFSET
  };
};

const inProximityRect = (nodeRect: Rect, proximityRect: RectCoordinates) => {
  const { x, y, width, height } = nodeRect;
  const { x1, x2, y1, y2 } = proximityRect;
  return x + width >= x1 && x <= x2 && y + height >= y1 && y <= y2;
};

const getDistanceAndAngleFromNode = (currentNodeRect: Rect, nodeRect: Rect) => {
  const cxCurrent = currentNodeRect.x + currentNodeRect.width / 2;
  const cyCurrent = currentNodeRect.y + currentNodeRect.height / 2;

  const cx = nodeRect.x + nodeRect.width / 2;
  const cy = nodeRect.y + nodeRect.height / 2;

  const dx = Math.abs(cx - cxCurrent);
  const dy = Math.abs(cy - cyCurrent);

  const distance = Math.sqrt(dx * dx + dy * dy);
  const angle = (Math.atan2(cy - cyCurrent, cx - cxCurrent) * 180) / Math.PI;

  /**
   * get the octant where the new node falls, the octant starts from -45 deg rather that 0
   * to cover the light pattern
   */
  const octant = octants[Math.floor(angle / 45).toString() as keyof typeof octants];

  return { distance, angle, octant };
};

export function getSuggestionConnections(
  nodesInProximity: NodeProximity[],
  currentNode: Node<NodeData>,
  usedPorts: Record<string, boolean>,
  allowMultiEdges: boolean = true
) {
  const closestNodeProximity = nodesInProximity[0];

  const connections: Connection[] = [];

  const closestNodeInLeft = closestNodeProximity.octant.startsWith('left');
  const closestNodeInRight = closestNodeProximity.octant.startsWith('right');

  const addConnection = (source: string, target: string) => {
    connections.push({
      id: doubleId(),
      target: target,
      source: source,
      sourcePort: 'dummy',
      targetPort: 'dummy'
    });
  };

  addConnection(
    closestNodeInLeft ? closestNodeProximity.nodeId : currentNode.id,
    closestNodeInLeft ? currentNode.id : closestNodeProximity.nodeId
  );

  // if the multi edges suggestion is not allowed return the single connection
  if (!allowMultiEdges) return connections;

  const filterHandle = (handle: NodeHandle) => filterNonUsedConnectablePorts(handle, usedPorts);

  let secondClosestNodeProximity: NodeProximity | undefined = undefined;

  /**
   * if multi connection is allowed try to connect to the other closest node, current we auto connect at most 2 nodes
   * the another connection should be established only if the user is keeping the dragged node little up or down the closest node
   * Also only if the current node has more than 1 input port empty
   */
  if (
    closestNodeInLeft &&
    currentNode.data.targetHandles.filter(filterHandle).length > 1 &&
    Math.abs(closestNodeProximity.angle) < 180 - NODE_ANGLE_DELTA
  ) {
    secondClosestNodeProximity = nodesInProximity.filter((nodeProximity) => nodeProximity.octant.startsWith('left'))[1];
  } else if (
    closestNodeInRight &&
    currentNode.data.sourceHandles.filter(filterHandle).length > 1 &&
    Math.abs(closestNodeProximity.angle) > NODE_ANGLE_DELTA
  ) {
    secondClosestNodeProximity = nodesInProximity.filter((nodeProximity) =>
      nodeProximity.octant.startsWith('right')
    )[1];
  }

  if (secondClosestNodeProximity) {
    addConnection(
      closestNodeInLeft ? secondClosestNodeProximity.nodeId : currentNode.id,
      closestNodeInLeft ? currentNode.id : secondClosestNodeProximity.nodeId
    );
  }

  return connections;
}

function getNodesInProximity(
  currentNodeRect: Rect,
  nodes: Node<NodeData>[],
  nodeRects: Record<string, Rect>,
  allowedDirections: NodeDirection[],
  filterHandle: (handle: NodeHandle) => boolean = () => true
) {
  /**
   * Find all the nodes which are in proximity of the currentNode rect
   */
  const proximityRect = getProximityRectCoordinates(currentNodeRect);
  const nodesInProximity: NodeProximity[] = [];

  nodes.forEach((node) => {
    // if node is virtual or subflow node ignore it.
    if (node.data.virtual || node.type === NodeType.subFlow) return;

    const nodeRect = nodeRects[node.id];

    // if the node is not in the proximity of the new node ignore it.
    if (!inProximityRect(nodeRect, proximityRect)) return;

    const hasUnconnectedOutputPorts = node.data.sourceHandles.some(filterHandle);
    const hasUnconnectedInputPorts = node.data.targetHandles.some(filterHandle);

    const { distance, angle, octant } = getDistanceAndAngleFromNode(currentNodeRect, nodeRect);

    // if the node is not on allowed direction ignore it
    if (!allowedDirections.includes(octant)) return;

    // if the distance is more than the proximity offset ignore it
    // add up half the node width and the half of current node width to determine the offset in circular pattern
    const allowedDistance = PROXIMITY_OFFSET + nodeRect.width / 2 + currentNodeRect.width / 2;
    if (distance > allowedDistance) return;

    /**
     * if the node is in the right side of the dragging node, than we should have unconnected input ports in the node
     * for other cases the dragging node can be connected through output port, either as sibling or as child
     */

    if (octant.startsWith('right') && !hasUnconnectedInputPorts) {
      return;
    } else if (!octant.startsWith('right') && !hasUnconnectedOutputPorts) {
      return;
    } else {
      nodesInProximity.push({
        distance,
        angle,
        octant,
        nodeId: node.id,
        node: node
      });
    }
  });

  return nodesInProximity.sort((a, b) => a.distance - b.distance);
}

function getProximityKey(nodesInProximity: NodeProximity[]) {
  return nodesInProximity.map((node) => `${node.nodeId}:${node.octant}`).join('-');
}

function useSuggestionPosition() {
  const store = useStoreApi();

  return ({
    process,
    upstreamProcesses,
    referenceNodeProximity,
    nodes
  }: {
    process: Process;
    upstreamProcesses: Process[];
    referenceNodeProximity: NodeProximity;
    nodes: Node<NodeData>[];
  }) => {
    const {
      width,
      height,
      transform: [tX, tY, tScale]
    } = store.getState();

    let leftCornerX = -tX / tScale;
    let leftCornerY = -tY / tScale;

    const cellDimension = NODE_DIMENSION + CELL_GAP;
    const referenceNode = nodes.find((node) => node.id === referenceNodeProximity.nodeId)!;

    /**
     * find how much closest node is shifted from grid so we calculate grid
     * in which closest node always starts from top left of a grid
     *
     * We do this, so new nodes are always aligned correctly to closest node
     */
    const xOffset = (referenceNode.position.x - CELL_GAP / 2 - leftCornerX) % cellDimension;
    const yOffset = (referenceNode.position.y - CELL_GAP / 2 - leftCornerY) % cellDimension;

    const containerWidth = width / tScale - xOffset;
    const containerHeight = height / tScale - yOffset;

    leftCornerX = leftCornerX + xOffset;
    leftCornerY = leftCornerY + yOffset;

    const { grid, noOfRows, noOfColumns, cellFullWidth, cellFullHeight } = getViewportGrids({
      points: nodes.map((node) => node.position),
      height: containerHeight,
      width: containerWidth,
      leftCornerX: leftCornerX,
      leftCornerY: leftCornerY,
      gap: CELL_GAP,
      cellHeight: NODE_DIMENSION,
      cellWidth: NODE_DIMENSION
    });

    const getCell = (x: number, y: number) => {
      const cellX = clamp(Math.round((y - CELL_GAP / 2 - leftCornerY) / cellFullHeight), 0, noOfRows - 1);
      const cellY = clamp(Math.round((x - CELL_GAP / 2 - leftCornerX) / cellFullWidth), 0, noOfColumns - 1);
      return [cellX, cellY];
    };

    const getEmptyCellPosition = (refCellX: number, refCellY: number) => {
      const maxCol = Math.min(refCellY + 4, noOfColumns - 1);
      // for columns keep checking on right side
      for (let i = refCellY; i < maxCol; i++) {
        // for rows keep checking alternatively upside and downside
        for (let j = 1; j < 3; j++) {
          const rowIndexUp = Math.max(refCellX - j, 0);
          const rowIndexDown = Math.min(refCellX + j, noOfRows - 1);
          const cellUp = grid[rowIndexUp][i];
          const cellDown = grid[rowIndexDown][i];

          if (!cellDown) {
            return [leftCornerX + i * cellFullWidth, leftCornerY + rowIndexDown * cellFullHeight + CELL_GAP / 2];
          } else if (!cellUp) {
            return [leftCornerX + i * cellFullWidth, leftCornerY + rowIndexUp * cellFullHeight];
          }
        }
      }
    };

    const { angle } = referenceNodeProximity;
    let x: number, y: number;

    let direction: Direction;

    /**
     * if there is no parent defined, it means the new node is sibling to closestNode.
     * Add it on top or bottom based on angle
     */
    if (!upstreamProcesses?.length) {
      if (angle >= 0) {
        x = referenceNode.position.x;
        y = referenceNode.position.y + 200;
      } else {
        x = referenceNode.position.x;
        y = referenceNode.position.y - 200;
      }
      direction = 'vertical';
    } else if (upstreamProcesses.length === 1) {
      // if there is single parent basically the referenceNode, then it will come right side of it
      x = referenceNode.position.x + 200;
      y = referenceNode.position.y;
      direction = 'horizontal';
    } else {
      // else find the vertical position in the center of both parent nodes.
      const verticalCenter = mean(upstreamProcesses.map((process) => process.metadata.y));

      x = referenceNode.position.x + 200;
      y = verticalCenter;
      direction = 'horizontal';
    }

    // get the dropping process grid
    const [cellX, cellY] = getCell(x, y);

    // if the grid is occupied, find another closest location
    if (grid[cellX][cellY]) {
      // if we are adding a node on vertical side, take the referenceNode as reference node
      const refX = direction === 'vertical' ? referenceNode.position.x : x;
      const refY = direction === 'vertical' ? referenceNode.position.y : y;

      const [refCellX, refCellY] = getCell(refX, refY);

      // if no available position, add it to x and y position with some offset
      const [newX, newY] = getEmptyCellPosition(refCellX, refCellY) || [x + CELL_GAP / 2, y + CELL_GAP / 2];

      x = Math.max(newX, x);
      y = newY;
    }

    process.metadata = {
      ...process.metadata,
      x,
      y
    };
  };
}

function getVirtualEdge(connection: GraphConnection) {
  return {
    id: connection.id,
    source: connection.source,
    sourceHandle: connection.sourcePort,
    target: connection.target,
    targetHandle: connection.targetPort,
    type: 'default',
    zIndex: 1,
    data: {
      readOnly: true,
      virtual: true,
      sourceLabel: '',
      targetLabel: '',
      points: []
    }
  } as Edge<EdgeData>;
}

function getUsedPorts(edges: Edge<EdgeData>[]) {
  const usedPorts: Record<string, boolean> = {};
  edges.forEach((edge) => {
    usedPorts[edge.sourceHandle as string] = true;
    usedPorts[edge.targetHandle as string] = true;
  });

  return usedPorts;
}

function setConnectionPorts(
  connection: GraphConnection,
  processes: GenericGraphProcesses<unknown, BaseProcessMetadata, BaseProcess<BaseProcessMetadata>, GraphConnection>,
  usedPorts: Record<string, boolean>,
  setTargetProcessSlug: boolean,
  suggestedProcess: GenericGraphProcesses<
    unknown,
    BaseProcessMetadata,
    BaseProcess<BaseProcessMetadata>,
    GraphConnection
  > = {}
) {
  const sourceProcess = processes[connection.source];
  const targetProcess = processes[connection.target];
  const sourceOutputPorts = sourceProcess.ports?.outputs || [];
  const targetInputPorts = targetProcess.ports?.inputs || [];

  const sourceNonUsedPorts = sourceOutputPorts.filter(({ id }) => !usedPorts[id]);
  const targetNonUsedPorts = targetInputPorts.filter(({ id }) => !usedPorts[id]);

  // prefer the non used ports, if not available to connect to used ports
  const sourcePortsToUse = sourceNonUsedPorts.length ? sourceNonUsedPorts : sourceOutputPorts;

  const targetIsOnTop = targetProcess.metadata.y < sourceProcess.metadata.y;
  const targetIsOnSameLine = targetProcess.metadata.y === sourceProcess.metadata.y;

  const sourceOutputPort =
    targetIsOnTop || targetIsOnSameLine ? sourcePortsToUse[0] : sourcePortsToUse[sourcePortsToUse.length - 1];

  const targetInputPort =
    targetIsOnSameLine || !targetIsOnTop ? targetNonUsedPorts[0] : targetNonUsedPorts[targetNonUsedPorts.length - 1];

  // set target port slug only if the target process is suggested process
  if (setTargetProcessSlug && suggestedProcess[targetProcess.id]) {
    // for target input port set the source label as port slug
    targetInputPort.slug =
      sourceOutputPorts.length > 1 ? sourceOutputPort.slug : spaceToUnderscore(sourceProcess.metadata.label);
  }

  connection.sourcePort = sourceOutputPort.id;
  connection.targetPort = targetInputPort.id;
}

export function useAutoPilotDrop<
  P extends BaseProcess<BaseProcessMetadata>,
  DragObject = unknown,
  DropResult = unknown
>({
  nodes,
  enabled,
  edges,
  accept = [],
  reactFlowWrapper,
  onDrop,
  getSuggestionGraph,
  getAllowedDirections
}: {
  nodes: Node<NodeData>[];
  edges: Edge<EdgeData>[];
  enabled: boolean;
  accept?: string[];
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
  onDrop: (params: { suggestionGraph?: SuggestionGraph; item: DropResult; position?: XYPosition }) => void;
  getSuggestionGraph: (
    nodesInProximity: NodeProximity[],
    item: DragObject,
    position: XYPosition,
    usedPorts: Record<string, boolean>
  ) => { suggestionGraph?: SuggestionGraph; process?: P; referenceNodeProximity: NodeProximity };
  getAllowedDirections: (item: DragObject) => NodeDirection[];
}) {
  const reactFlowInstance = useReactFlow();

  const gemSpecs = useSpecs();

  const { suggestion, shiftKeyPressed, setSuggestion } = useAutoPilot();
  const store = useStoreApi();

  const lastNodesInProximity = useRef<NodeProximity[]>([]);
  const processToDrop = useRef<P>();
  const toastRef = useRef<ToastUtilReturnType | null>(null);
  const nodeRects = useRef<Record<string, Rect>>();

  const resetSuggestion = () => {
    lastNodesInProximity.current = [];
    processToDrop.current = undefined;
    setSuggestion(undefined);

    if (toastRef.current) {
      toastRef.current.close();
      toastRef.current = null;
    }
  };

  const onHover = useThrottle(
    (item: DragObject, monitor: DropTargetMonitor<DragObject, DropResult>) => {
      let position: XYPosition;

      // if we haven't calculated other nodes rect calculate it once and memoize it so on every mouse move we don't calculate it again
      if (!nodeRects.current) {
        nodeRects.current = getNodeRectMap(nodes, store.getState().nodeInternals);
      }

      // if shiftKeyPressed is pressed don't show the suggestion
      if (shiftKeyPressed) {
        resetSuggestion();
        return;
      }

      const clientOffset = monitor?.getClientOffset();

      if (reactFlowWrapper.current && reactFlowInstance && clientOffset) {
        const { x, y } = clientOffset;
        const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
        position = reactFlowInstance.project({
          x: x - reactFlowBounds.left,
          y: y - reactFlowBounds.top
        });

        const positionRelativeToCenter = {
          x: position.x - NODE_DIMENSION / 2,
          y: position.y - NODE_DIMENSION / 2
        };

        const currentNodeRect: Rect = {
          x: positionRelativeToCenter.x,
          y: positionRelativeToCenter.y,
          width: NODE_DIMENSION,
          height: NODE_DIMENSION
        };

        const allowedDirections = getAllowedDirections(item);

        const _lastNodesInProximity = lastNodesInProximity.current;
        const usedPorts = getUsedPorts(edges);
        const nodesInProximity = getNodesInProximity(
          currentNodeRect,
          nodes,
          nodeRects.current,
          allowedDirections,
          (handle) => filterNonUsedConnectablePorts(handle, usedPorts)
        );

        lastNodesInProximity.current = nodesInProximity;

        // if there is no closest node no need to auto pilot, also reset previous suggestions
        if (!nodesInProximity.length) {
          resetSuggestion();
          return;
        }

        // we don't need to create new suggestion graph instead just change the position of the primary node if user is on same octane
        const currentProximityKey = getProximityKey(nodesInProximity);
        const lastProximityKey = _lastNodesInProximity ? getProximityKey(_lastNodesInProximity) : '';

        if (currentProximityKey === lastProximityKey) {
          if (processToDrop.current) {
            const { id, metadata } = processToDrop.current;
            const { updateNodePositions } = store.getState();
            updateNodePositions(
              [
                {
                  id,
                  position: positionRelativeToCenter,
                  positionAbsolute: positionRelativeToCenter,
                  distance: { x: 0, y: 0 }
                }
              ],
              true,
              false
            );

            // update process metadata
            metadata.x = positionRelativeToCenter.x;
            metadata.y = positionRelativeToCenter.y;
          }
          return;
        }

        const { suggestionGraph, process, referenceNodeProximity } = getSuggestionGraph(
          nodesInProximity,
          item,
          positionRelativeToCenter,
          usedPorts
        );
        processToDrop.current = process;

        let suggestion = suggestionGraph
          ? {
              graph: suggestionGraph,
              nodesInProximity,
              referenceNodeProximity
            }
          : undefined;

        if (suggestion && !toastRef.current) {
          toastRef.current = toast.info({
            content: 'Hold shift to disable suggestion',
            closeable: false,
            width: `300px`
          });
        }

        setSuggestion(suggestion);
      }
      return item as unknown as DropResult;
    },
    200,
    { trailing: true }
  );
  const fakeDrop = useRef<HTMLDivElement>() as unknown as ConnectDropTarget;
  const _accept = [...gemSpecsToGemTypeString(gemSpecs), ...accept];
  const drop = useDrop<DragObject, DropResult>({
    accept: _accept,
    hover: onHover,
    drop: (item, monitor) => {
      let position: XYPosition | undefined = undefined;

      // if there are no added suggest add the node
      if (reactFlowWrapper.current && reactFlowInstance) {
        const { x, y } = monitor.getClientOffset() as XYPosition;
        const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
        position = reactFlowInstance.project({
          x: x - reactFlowBounds.left,
          y: y - reactFlowBounds.top
        });
      }

      // apply the updated position on suggestion graph process
      if (processToDrop.current && suggestion?.graph) {
        const { id, metadata } = processToDrop.current;
        suggestion.graph.processes[id].metadata = metadata;
      }

      // if there are suggestion send those added suggestions
      onDrop({
        suggestionGraph: suggestion?.graph,
        item: item as unknown as DropResult,
        position
      });

      resetSuggestion();
      onHover.cancel();

      return undefined;
    },
    collect: (monitor) => {
      // reset the nodes if its not dropped and moved out
      if (
        enabled &&
        monitor.getItem() &&
        !monitor.isOver() &&
        !monitor.didDrop() &&
        nodes.some((node) => node.data.virtual)
      ) {
        resetSuggestion();
      }

      if (nodeRects.current && (monitor.didDrop() || !monitor.isOver())) {
        // reset the nodes suggestion after successful drop
        nodeRects.current = undefined;
        onHover.cancel();
      }
    }
  })[1];

  return { drop: enabled ? drop : fakeDrop };
}

export function usePlotSuggestionGraph<G extends CommonGraph>({
  initialized,
  graph,
  nodes,
  edges,
  setNodes,
  setEdges,
  getNodeFromProcess
}: {
  initialized: boolean;
  graph: G;
  nodes: Node<NodeData>[];
  edges: Edge<EdgeData>[];
  setNodes: React.Dispatch<React.SetStateAction<Node<NodeData>[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge<EdgeData>[]>>;
  getNodeFromProcess: (process: Process) => Node<NodeData>;
}) {
  const { suggestion } = useAutoPilot();
  const setSuggestionPosition = useSuggestionPosition();

  // plot the virtual node
  useWatch(() => {
    if (!initialized) {
      return;
    }

    const permanentNodes = nodes.filter((node) => !node.data.virtual);
    const permanentEdges = edges.filter((edge) => !edge.data?.virtual);
    const hasVirtualNodes = nodes.some((node) => node.data.virtual);
    const hasVirtualEdges = edges.some((edge) => edge.data?.virtual);

    const reset = () => {
      // if there are no virtual nodes or edges, don't reset the nodes
      if (!hasVirtualNodes && !hasVirtualEdges) {
        return;
      }
      setNodes((previous) => previous.filter((n) => !n.data.virtual));
      setEdges((previous) => previous.filter((e) => !e.data?.virtual));
    };

    // if there is no suggestion reset the nodes
    if (
      !suggestion ||
      (Object.keys(suggestion.graph.processes).length === 0 && suggestion.graph.connections.length === 0)
    ) {
      reset();
      return;
    }

    const { graph: suggestionGraph, nodesInProximity, referenceNodeProximity } = suggestion;
    const _nodes = [...permanentNodes];
    const _edges = [...permanentEdges];

    if (nodesInProximity?.length) {
      const usedPorts = getUsedPorts(_edges);

      const { processes, connections } = suggestionGraph;

      const allProcesses = { ...graph.processes, ...processes };

      Object.values(processes).forEach((process) => {
        const upstreamProcesses = connections
          .filter((connection) => connection.target === process.id)
          .map((connection) => allProcesses[connection.source]);

        if ((process.metadata.x === AUTO_POSITION || process.metadata.y === AUTO_POSITION) && referenceNodeProximity) {
          setSuggestionPosition({ process, referenceNodeProximity, upstreamProcesses, nodes: _nodes });
        }
        const suggestedNode = getNodeFromProcess(process);
        _nodes.push(suggestedNode);
      });

      connections.forEach((connection) => {
        // if connection don't have sourcePort and target port add it.
        if (!connection.sourcePort || !connection.targetPort) {
          setConnectionPorts(connection, allProcesses, usedPorts, true, processes);
          // add target port as used
          usedPorts[connection.targetPort] = true;
        }

        _edges.push(getVirtualEdge(connection));
      });

      /**
       * if we have any process whose some of the ports are not connected,
       * remove the upstream process slugs from all to avoid inconsistent behaviour
       */
      Object.values(processes).forEach((process) => {
        if (process.ports?.inputs?.some((port) => !usedPorts[port.id])) {
          process.ports.inputs.forEach((port, index) => {
            port.slug = `in${index}`;
          });
        }
      });
    } else {
      const { processes, connections } = suggestionGraph;
      connections.forEach((connection) => {
        _edges.push(getVirtualEdge(connection));
      });
      Object.values(processes).forEach((process) => {
        const suggestedNode = getNodeFromProcess(process);
        _nodes.push(suggestedNode);
      });
    }

    //update nodes and edges
    setNodes(_nodes);
    setEdges(_edges);
  }, [suggestion, graph, initialized]);
}

export function useAutoPilotEdge<G extends CommonGraph>({
  nodes,
  edges,
  graph,
  graphPath,
  onNodeDrag,
  onNodeDragStop,
  getAllowedDirections,
  allowMultiEdges = true,
  forwardSlug
}: {
  nodes: Node<NodeData>[];
  edges: Edge<EdgeData>[];
  graph: G;
  graphPath: string;
  onNodeDrag?: NodeDragHandler;
  onNodeDragStop?: OnNodeDragStopHandler;
  getAllowedDirections: (node: Node<NodeData>, usedPorts: Record<string, boolean>) => NodeDirection[];
  allowMultiEdges?: boolean;
  forwardSlug?: boolean;
}) {
  const { method: propertiesDidChange } = useLSPMethod(LSP.Method.propertiesDidChange);
  const { method: propertiesDidSave } = useLSPMethod(LSP.Method.propertiesDidSave);
  const forwardOutputSlugs = useForwardOutputSlugs();

  const { suggestion, shiftKeyPressed, setSuggestion } = useAutoPilot();
  const lastNodesInProximity = useRef<NodeProximity[]>([]);
  const toastRef = useRef<ToastUtilReturnType | null>(null);
  // cache for calculated node rects
  const nodeRects = useRef<Record<string, Rect>>();

  // cache for calculated cyclic connection
  const cyclicConnections = useRef<Record<string, boolean>>({});

  const store = useStoreApi();

  const resetSuggestion = usePersistentCallback(() => {
    lastNodesInProximity.current = [];
    setSuggestion(undefined);

    if (toastRef.current) {
      toastRef.current.close();
      toastRef.current = null;
    }
  });

  const _onNodeDrag: NodeDragHandler = (event, currentNode: Node<NodeData>, selectNodes: Node<NodeData>[]) => {
    onNodeDrag?.(event, currentNode, selectNodes);

    // if shiftKeyPressed is pressed don't show the suggestion
    if (shiftKeyPressed) {
      return;
    }

    // if the dragged node is the subflow no need to show any suggestion
    if (!currentNode || currentNode.type === NodeType.subFlow) {
      return;
    }

    const { nodeInternals } = store.getState();

    if (!nodeRects.current) {
      // if we haven't calculated other nodes rect calculate it once and memoize it so on every mouse move we don't calculate it again
      nodeRects.current = getNodeRectMap(nodes, nodeInternals);
    }

    // if multiple nodes are selected no need to show suggestion
    if (selectNodes.length > 1) return;

    const permanentEdges = edges.filter((edge) => !edge.data?.virtual);
    const usedPorts = getUsedPorts(permanentEdges);

    const allowedDirections = getAllowedDirections(currentNode, usedPorts);

    const _lastNodesInProximity = lastNodesInProximity.current;
    const remainingNodes = nodes.filter((node) => node.id !== currentNode.id);
    const currentNodeRect = getNodeRect(currentNode, nodeInternals);
    const nodesInProximity = getNodesInProximity(
      currentNodeRect,
      remainingNodes,
      nodeRects.current,
      allowedDirections,
      (handle) => filterNonUsedConnectablePorts(handle, usedPorts)
    );

    lastNodesInProximity.current = nodesInProximity;

    // we don't need to create new suggestion graph instead just change the position of the primary node if user is on same octane
    const currentProximityKey = getProximityKey(nodesInProximity);
    const lastProximityKey = _lastNodesInProximity ? getProximityKey(_lastNodesInProximity) : '';

    // if there is no closest node no need to auto pilot, also reset previous suggestions
    if (!nodesInProximity.length) {
      resetSuggestion();
      return;
    }

    // we don't need to create new suggestion graph if its on the same place
    if (currentProximityKey === lastProximityKey) {
      return;
    }

    // get the closest node
    const closestNodeProximity = nodesInProximity[0];

    let connections: Connection[] = getSuggestionConnections(nodesInProximity, currentNode, usedPorts, allowMultiEdges);

    /**
     * check if the connection is cyclic and remove if it is, also cache the information so
     * we don't calculate it again as cyclic check is expensive
     */
    connections = connections.filter((connection) => {
      const key = `${connection.source}-${connection.target}`;
      if (cyclicConnections.current[key] === undefined) {
        cyclicConnections.current[key] = isCyclicGraph(permanentEdges, connection.target, connection.source);
      }
      return !cyclicConnections.current[key];
    });

    // if not connections are found reset the suggestion
    if (!connections.length) {
      resetSuggestion();
      return;
    }

    const currentProcess = graph.processes[currentNode.id];
    const updatedProcess = {
      ...currentProcess,
      metadata: {
        ...currentProcess.metadata,
        ...currentNode.position
      }
    };

    connections.forEach((connection) => {
      setConnectionPorts(connection, { ...graph.processes, [currentNode.id]: updatedProcess }, usedPorts, false);
      usedPorts[connection.targetPort] = true;
      usedPorts[connection.sourcePort] = true;
    });

    setSuggestion({
      nodesInProximity,
      referenceNodeProximity: closestNodeProximity,
      graph: {
        processes: {},
        connections
      }
    });

    // show toast to disable suggestion
    if (!toastRef.current) {
      toastRef.current = toast.info({
        content: 'Hold shift to disable auto connection',
        closeable: false,
        width: `320px`
      });
    }
  };

  const _onNodeDragStop: NodeDragHandler = (event, node) => {
    onNodeDragStop?.(node?.id as string);
    const connections = suggestion?.graph.connections;

    if (connections?.length) {
      if (forwardSlug) {
        forwardOutputSlugs(graph, graphPath, connections);
      }

      propertiesDidChange({
        property: getConnectionPath(graphPath),
        value: [...graph.connections, ...connections]
      });

      propertiesDidSave();
    }

    resetSuggestion();

    // reset the nodes suggestion after drag stop
    nodeRects.current = undefined;

    // reset the cyclic connections cache
    cyclicConnections.current = {};
  };

  useEffect(() => {
    if (suggestion && shiftKeyPressed) {
      resetSuggestion();
    }
  }, [suggestion, shiftKeyPressed, resetSuggestion]);

  return {
    onNodeDrag: _onNodeDrag,
    onNodeDragStop: _onNodeDragStop
  };
}

export function createSuggestionGraph<T extends BaseProcess<BaseProcessMetadata>>(
  process: T,
  updateProcess?: (currentProcess: T, baseProcess: T) => void
) {
  const processes: SuggestionGraph['processes'] = {};
  const connections: SuggestionGraph['connections'] = [];

  const addNewProcess = (newProcess: T) => {
    updateProcess?.(newProcess, process);
    processes[newProcess.id] = newProcess;
  };

  const addNewConnection = (source: string, target: string) => {
    connections.push({
      id: doubleId(),
      source,
      target,
      sourcePort: '',
      targetPort: ''
    });
  };

  const getGraph = () => {
    if (!Object.keys(processes).length) return;

    return {
      processes,
      connections
    };
  };

  return {
    addNewProcess,
    addNewConnection,
    getGraph
  };
}
